import merge from "lodash/merge";
import { get, writable, derived, type Writable } from "svelte/store";
import type { FoojiData } from "../../types/campaign";
import type { Gift } from "../../types/gift";
import pickBy from "lodash/pickBy";
import type { AnonymousAuthData } from "../api/campaign";
import omit from "lodash/omit";
import type { MenuItem } from "../helpers/steps/menuItems";

export type Route = {
  id: string;
  path: string;
  public: boolean;
  requiresAuth: boolean;
  component: string;
};

export enum NavigationType {
  Next = "next",
  Prev = "prev"
}

export type SocialGiftingSettings = AnonymousAuthData & {
  checksum_ignore: string[];
  items_chosen?: { item_key: string; variation_key: string; count: 1 }[];
};

export type MenuItemSelected = {
  item_key: string;
  variation_key: string;
  api?: string; // FIXME: This should be required
};

export type TurnstileTokenData = {
  token?: string;
  validUntil?: string,
  retryAt?: string,
  hasCompletedCaptchaProcess?: boolean
};

export type Session = {
  startsAt?: string;
  foojiData?: FoojiData;
  routes: Route[];
  currentPath: string;
  jwt: string | undefined;
  stepAllowList: string[];
  language: string;
  lastUserStatusUpdate?: string;
  authCompleted?: boolean;
  gift?: Gift;
  searchParams: Record<string, string>;
  socialGifting?: SocialGiftingSettings;
  socialGiftingMode?: "full" | "select" | "bundle";
  internalProcessKey?: string;
  authState?: string;
  isAuthenticating?: boolean;
  socialGiftBundleSelectedItems?: { itemKey: string; variation?: string }[];
  socialGiftBundleMenuItems?: MenuItem[];
  multipleSelectionSelectedItems?: MenuItemSelected[];
  isExpired?: boolean;
  turnstileTokenData?: TurnstileTokenData;
};

export const initStore = () => {
  const defaultState: Session = {
    routes: [],
    jwt: undefined,
    currentPath: "",
    stepAllowList: [],
    language: "en",
    searchParams: {}
  };

  const initialState = JSON.parse(sessionStorage.getItem("sessionStore") || "{}") as Session;
  const store = writable<Session>(merge(defaultState, initialState));

  store.subscribe(($store) => {
    sessionStorage.setItem("sessionStore", JSON.stringify($store));
  });

  return store;
};

export const sessionStore = initStore();

export const getPath = (direction: NavigationType = NavigationType.Next) => {
  const store = get(sessionStore);
  const location = store.currentPath;
  const currentIndex = store.routes.findIndex((route: Route) => route.path === location);
  const routeIndex = direction === NavigationType.Next ? currentIndex + 1 : currentIndex - 1;
  return store.routes[routeIndex].path;
};

export const updateSession = (update: Partial<Session>) => {
  sessionStore.update((data) => {
    return {
      ...data,
      ...update
    };
  });
};

export const updateSearchParams = (searchParams: Record<string, string>) => {
  updateSession({ searchParams: { ...get(sessionStore).searchParams, ...searchParams } });
};

export const clearSearchParam = (paramName: string) => {
  updateSession({ searchParams: pickBy(get(sessionStore).searchParams, (_, key) => key !== paramName) });
};

export const clearAllSearchParams = () => {
  updateSession({ searchParams: {} });
};

export const setLanguage = (language: string) => {
  updateSession({ language });
};

export const setSelectedGift = (gift: Gift | undefined) => {
  updateSession({ gift });
};

export const clearSelectedGifts = () =>
  sessionStore.update((data) => omit(data, "gift", "multipleSelectionSelectedItems"));

export const setSocialGiftBundleItems = (
  selectedItems: { itemKey: string; variation?: string }[],
  menuItems: MenuItem[]
) => {
  updateSession({ socialGiftBundleSelectedItems: selectedItems, socialGiftBundleMenuItems: menuItems });
};

export const setSocialGiftBundleSelectedItemVariation = (index: number, variation: string) => {
  updateSession({
    socialGiftBundleSelectedItems: get(sessionStore).socialGiftBundleSelectedItems?.map((item, i) => {
      return i === index ? { ...item, variation } : item;
    })
  });
};

export const upsertMultipleSelectionItem = (item: MenuItemSelected) => {
  const existingItem = get(sessionStore).multipleSelectionSelectedItems?.find((i) => i.item_key === item.item_key);

  if (existingItem) {
    updateSession({
      multipleSelectionSelectedItems: get(sessionStore).multipleSelectionSelectedItems?.map((i) =>
        i.item_key === item.item_key ? item : i
      )
    });
  } else {
    updateSession({
      multipleSelectionSelectedItems: [...(get(sessionStore).multipleSelectionSelectedItems || []), item]
    });
  }
};

export const removeMultipleSelectionItem = (itemKey: string) => {
  updateSession({
    multipleSelectionSelectedItems:
      get(sessionStore).multipleSelectionSelectedItems?.filter((i) => i.item_key !== itemKey)
  });
};

function hasRouteAtIndex(sessionStore: Writable<Session>, indexOffset: number) {
  return derived(sessionStore, ($sessionStore: Session) => {
    const location = $sessionStore.currentPath;
    const currentIndex = $sessionStore.routes.findIndex((route: Route) => route.path === location);
    const targetIndex = currentIndex + indexOffset;
    return !!$sessionStore.routes[targetIndex]?.path;
  });
}

export const hasNextPage = hasRouteAtIndex(sessionStore, 1);
export const hasPrevPage = hasRouteAtIndex(sessionStore, -1);
